"use client";

import Image from "next/image";
import React from "react";
import { useTranslations } from "next-intl";
import { trackCtaClicked } from "@/lib/analytics";
import { useItemPricing, formatINR, FALLBACK_SELLING_PRICE } from "@/lib/useItemCost";

const MarqueeContent = ({ text }: { text: string }) => {
  const items = text.split(" • ");
  return (
    <span className="text-white font-bold text-[0.65rem] sm:text-xs md:text-sm tracking-wider uppercase flex items-center shrink-0">
      {items.map((item, i) => (
        <React.Fragment key={i}>
          <span className="px-6 md:px-10 whitespace-nowrap">{item}</span>
          {i < items.length - 1 && (
            <span className="w-1.5 h-1.5 rounded-full bg-white/60 shrink-0" />
          )}
        </React.Fragment>
      ))}
      <span className="w-1.5 h-1.5 rounded-full bg-white/60 shrink-0 mx-6 md:mx-10" />
    </span>
  );
};

export default function Hero() {
  const t = useTranslations("prebookCustomer");
  const tHero = useTranslations("hero");
  const { itemCost, mrp, discountAmount } = useItemPricing();

  return (
    <div>
      {/* Hero Section */}
      <div className="hero-bg top-24 left-0 w-full relative">
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-20 pt-6 md:pt-10 lg:pt-14">
          <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-8">
            {/* Left: Text Content */}
            <div className="flex-1 max-w-2xl text-center lg:text-left">
              {/* Pre-Booking Badge */}
              <p
                className="text-white font-semibold text-lg sm:text-xl md:text-2xl lg:text-3xl mb-2 leading-tight"
                style={{ fontFamily: "Marble, sans-serif" }}
              >
                {t("hero.badge")}
              </p>

              {/* Headline */}
              <h1 className="text-white font-black text-3xl sm:text-4xl md:text-5xl lg:text-[3.5rem] leading-none mb-2 md:mb-3">
                {t("hero.headline")}
              </h1>

              {/* Product Name */}
              <h2 className="text-white font-bold text-base sm:text-lg md:text-xl uppercase tracking-wide mb-3 md:mb-4">
                {t("hero.productName")}
              </h2>

              {/* Description */}
              <p className="text-white/80 text-sm md:text-base leading-relaxed mb-5 md:mb-6 max-w-lg mx-auto lg:mx-0">
                {t("hero.description")}
              </p>

              {/* Pricing Row */}
              <div className="flex items-baseline gap-3 mb-5 justify-center lg:justify-start flex-wrap">
                {/* Hidden for now — MRP/save messaging turned off
                <span className="ttext-black font-semibold  md:text-base line-through">
                  {t("hero.mrp")}
                </span>
                */}
                <span className="text-white font-black text-3xl md:text-4xl lg:text-5xl">
                  ₹{formatINR(itemCost ?? FALLBACK_SELLING_PRICE)}
                </span>
                {itemCost && mrp && discountAmount > 0 && (
                  <span className="text-white/70 font-bold text-lg md:text-xl lg:text-2xl line-through">
                    ₹{formatINR(mrp)}
                  </span>
                )}
                <span className="text-white font-bold text-lg md:text-xl lg:text-2xl">
                  {tHero("offerSlide.priceNote")}
                </span>
                {/* Hidden for now — MRP/save messaging turned off
                <span className="bg-yellow-400 text-primary bg-white rounded-full font-extrabold text-xs md:text-sm px-3 py-1 ">
                  {t("hero.save")}
                </span>
                */}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row items-center lg:items-start gap-3 sm:gap-4 mb-4">
                <button
                  type="button"
                  onClick={() => { trackCtaClicked({ cta: "book_now", page: "prebook_customer" }); document.getElementById("preBookSection")?.scrollIntoView({ behavior: "smooth" }); }}
                  className="cta-hero inline-flex items-center bg-white text-primary font-bold text-sm md:text-base uppercase tracking-wider px-6 md:px-8 py-3.5 md:py-4 rounded rounded-tl-2xl rounded-br-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
                >
                  {t("hero.prebookBtn")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    trackCtaClicked({ cta: "full_info", page: "prebook_customer" });
                    const url = t("hero.fullInfoLink");
                    window.open(url, "_blank");
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "";
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  }}
                  className="inline-flex items-center gap-2 text-white font-bold text-sm md:text-base uppercase tracking-wider px-6 md:px-8 py-3.5 md:py-4 border-2 border-white/50 rounded rounded-tl-2xl rounded-br-2xl hover:bg-white/10 transition-all duration-300"
                >
                  {t("hero.fullInfo")}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 16l-5-5h3V4h4v7h3l-5 5zm-7 2h14v2H5v-2z" />
                  </svg>
                </button>
              </div>

            </div>

            {/* Right: Stick Image */}
            <div className="flex-shrink-0 lg:w-[38%] flex justify-center lg:justify-end">
              <Image
                src="/images/shopPartner/HeroStick.webp"
                alt="KST Stick Device"
                width={300}
                height={350}
                className="stick-float w-44 sm:w-52 md:w-64 lg:w-[320px] object-contain"
                style={{
                  filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.25))",
                }}
                priority
              />
            </div>
          </div>
        </div>
      </div>

      {/* Marquee Banner */}
      <div className="bg-gradient-to-r from-[#C2185B] via-[#AD1457] to-[#C2185B] py-3 md:py-3.5 overflow-hidden">
        <div className="marquee-track">
          {[...Array(4)].map((_, i) => (
            <MarqueeContent key={i} text={t("ticker")} />
          ))}
        </div>
      </div>
    </div>
  );
}
